/* =====================================================================
 * SikaRoots cart drawer — mutations for the bundle ladder.
 * Ported unchanged from Metabolae (2026-10-17) except the shipping-protection
 * opt-in mode (data-fc-sp-optin). Handlers for Metabolae-only upsell cards are
 * kept but inert: their buttons are never rendered on SikaRoots.
 *
 * Everything the drawer DISPLAYS is rendered by Liquid. This file only handles
 * the two actions Shopify has no declarative equivalent for:
 *   1. Remove a line.
 *   2. Move a line between bundle tiers.
 * After either, the drawer is re-rendered from the server through the Section
 * Rendering API, so prices, savings, the upgrade card and the cart bubble all
 * recompute in Liquid. There is no client-side pricing model to drift.
 *
 * Listeners are delegated on document, so they survive the innerHTML swap that
 * re-rendering performs. Do not rebind on individual buttons.
 * ===================================================================== */
(function () {
  'use strict';

  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var mutating = false;

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function showError(msg) {
    var el = document.querySelector('#CartDrawer .mcd-err');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function busy(key, state) {
    if (!key) return;
    var rows = document.querySelectorAll('#CartDrawer .mcd-line');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-key') === key) {
        if (state) rows[i].setAttribute('data-busy', '1');
        else rows[i].removeAttribute('data-busy');
      }
    }
  }

  function innerHTMLFrom(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  /**
   * Mirrors Dawn's CartDrawer.renderContents(), including the overlay rebind —
   * the overlay lives INSIDE #CartDrawer, so replacing that innerHTML destroys
   * the listener Dawn attached at construction. Skip the rebind and
   * click-outside-to-close silently dies after the first tier change.
   */
  function applySections(state) {
    var sections = state && state.sections;
    if (!sections) return;

    var drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      var target = document.querySelector('#CartDrawer');
      var markup = innerHTMLFrom(drawerHTML, '#CartDrawer');
      if (target && markup !== null) target.innerHTML = markup;
    }

    var bubbleHTML = sections['cart-icon-bubble'];
    if (bubbleHTML) {
      var bubble = document.getElementById('cart-icon-bubble');
      var bubbleMarkup = innerHTMLFrom(bubbleHTML, '.shopify-section');
      if (bubble && bubbleMarkup !== null) bubble.innerHTML = bubbleMarkup;
    }

    var drawer = drawerEl();
    if (drawer) {
      if (state.item_count === 0) drawer.classList.add('is-empty');
      else drawer.classList.remove('is-empty');

      var overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') {
        overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }

    syncSP();
  }

  function postJSON(url, body) {
    body.sections = SECTIONS;
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || 'cart error');
        return data;
      });
    });
  }

  /* If the given `updates` would leave NO real product lines, also drop shipping
   * protection in the SAME call — so emptying the cart goes straight to the
   * empty state in one round-trip instead of flashing an "SP-only" cart first.
   * (SP is kept out of the .mcd-line list, so line count = real products.) */
  function dropSPIfEmptying(updates) {
    var lines = document.querySelectorAll('#CartDrawer .mcd-line');
    var remaining = 0;
    for (var i = 0; i < lines.length; i++) {
      var k = lines[i].getAttribute('data-key');
      var v = updates[k];
      if (v === undefined || v > 0) remaining++;
    }
    if (remaining > 0) return;
    var block = spBlock();
    var spKey = block && block.getAttribute('data-fc-sp-key');
    if (spKey) updates[spKey] = 0;
  }

  /**
   * Remove one line, keyed by line KEY.
   * CAUTION: not idempotent — a second tap on an already-removed key returns
   * 422, which is why the button is disabled for the duration.
   */
  function removeLine(key, btn) {
    if (!key || mutating) return;
    mutating = true;
    busy(key, true);
    if (btn) btn.disabled = true;

    var updates = {};
    updates[key] = 0;
    // Removing a Kaching bundle's paid line takes its free gifts with it, in the
    // SAME call — so the buyer never sees (or checks out) an orphaned $0 cart.
    if (btn && btn.hasAttribute('data-fc-bundle-main')) {
      var giftEls = document.querySelectorAll('#CartDrawer [data-fc-bundle-gift]');
      for (var gi = 0; gi < giftEls.length; gi++) updates[giftEls[gi].getAttribute('data-key')] = 0;
    }
    dropSPIfEmptying(updates);
    postJSON('/cart/update.js', { updates: updates })
      .then(applySections)
      .catch(function () {
        busy(key, false);
        if (btn) btn.disabled = false;
        showError("That didn't go through — please try again.");
      })
      .then(function () {
        mutating = false;
      });
  }

  /**
   * Move a line to another bundle tier.
   *
   * CAUTION: Shopify's /cart/change.js CANNOT change a line's variant, so this
   * is necessarily remove + add. We ADD FIRST and remove second — if the add
   * fails the buyer still has their original cart, whereas remove-first would
   * empty it on a failed add.
   *
   * The existing selling plan is carried forward, because the store attaches one
   * by default and dropping it would silently convert the purchase.
   */
  function setTier(key, toVariantId, plan, btn) {
    if (!key || !toVariantId || mutating) return;
    mutating = true;
    busy(key, true);
    if (btn) btn.disabled = true;

    var addBody = { id: toVariantId, quantity: 1 };
    if (plan) addBody.selling_plan = plan;

    postJSON('/cart/add.js', addBody)
      .then(function () {
        var updates = {};
        updates[key] = 0;
        return postJSON('/cart/update.js', { updates: updates });
      })
      .then(applySections)
      .catch(function () {
        busy(key, false);
        if (btn) btn.disabled = false;
        showError("We couldn't change the bundle. Your cart is unchanged.");
      })
      .then(function () {
        mutating = false;
      });
  }

  /**
   * Kaching bundle paid line (fc-bundle-box). The paid line's quantity stepper
   * keeps the gift lines in sync with the quantity's tier:
   *   qty >= a tier's qty  ->  that tier's gifts (highest matching tier wins).
   * Tier map (gift variant ids + per-tier Kaching gift ids + the bundle's group
   * id) is written to localStorage ('fc_bundle_map') by the bundle ATC — kept
   * out of cart attributes so it never pollutes orders in admin. New gift lines
   * reuse the ORIGINAL group id so Kaching still zeroes them. If the map is
   * absent (storage cleared / returning session) the stepper degrades to a
   * plain quantity change.
   */
  function fcApplicableTier(q, tiers) {
    var chosen = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].qty <= q) chosen = tiers[i];
    }
    return chosen || tiers[0] || null;
  }

  function setBundleQty(key, newQty, curQty, btn) {
    if (!key || isNaN(newQty) || mutating) return;
    var raw = null;
    try { raw = localStorage.getItem('fc_bundle_map'); } catch (e) { raw = null; }
    var map = null;
    try { map = raw ? JSON.parse(raw) : null; } catch (e) { map = null; }
    // No map (cart from an earlier session, or storage cleared) -> behave like a
    // plain quantity change (gifts simply stay as they are).
    if (!map || !map.tiers || !map.tiers.length) { setQty(key, newQty, btn); return; }

    function giftKeys() {
      var out = [];
      var els = document.querySelectorAll('#CartDrawer [data-fc-bundle-gift]');
      for (var i = 0; i < els.length; i++) out.push(els[i].getAttribute('data-key'));
      return out;
    }

    // Below the lowest tier: remove the whole bundle — paid line + every gift
    // (and SP if it would be the only thing left).
    if (newQty <= 0) {
      mutating = true; busy(key, true); if (btn) btn.disabled = true;
      var rm = {};
      rm[key] = 0;
      giftKeys().forEach(function (k) { rm[k] = 0; });
      dropSPIfEmptying(rm);
      postJSON('/cart/update.js', { updates: rm })
        .then(applySections)
        .catch(function () { busy(key, false); if (btn) btn.disabled = false; showError("That didn't go through — please try again."); })
        .then(function () { mutating = false; });
      return;
    }

    var oldTier = fcApplicableTier(curQty, map.tiers);
    var newTier = fcApplicableTier(newQty, map.tiers);

    // Same tier -> gifts are unchanged, only the quantity moves.
    if (oldTier === newTier) { setQty(key, newQty, btn); return; }

    // Tier changed -> rebuild the gift set. Add the new tier's gifts first
    // (all-or-nothing), then set the paid qty and drop the previous gift lines
    // in one update — add-first so a failed add never corrupts the cart. A
    // repeated gift (cookbook) carries a different Kaching gift id per tier, so
    // it is genuinely re-added, not kept.
    mutating = true; busy(key, true); if (btn) btn.disabled = true;

    var addItems = (newTier.gifts || []).map(function (g) {
      return {
        id: g.id,
        quantity: 1,
        properties: { __kaching_bundles: JSON.stringify({ id: map.group, deal: map.deal, gift: g.gid }) }
      };
    });

    var addStep = addItems.length ? postJSON('/cart/add.js', { items: addItems }) : Promise.resolve();
    addStep
      .then(function () {
        var updates = {};
        updates[key] = newQty;
        giftKeys().forEach(function (k) { updates[k] = 0; });
        return postJSON('/cart/update.js', { updates: updates });
      })
      .then(applySections)
      .catch(function () { busy(key, false); if (btn) btn.disabled = false; showError("We couldn't update the bundle. Your cart is unchanged."); })
      .then(function () { mutating = false; });
  }

  /**
   * FC ABC upsell cards (fc-cart-upsell.liquid). One /cart/add.js call with an
   * items array — Shopify adds all-or-nothing, so variant C can never end up
   * with the cinnamon added but the vitamin missing. Selling plans ride along
   * per item (immediate subscription). After the re-render the cards vanish
   * server-side because the vitamin is now in the cart.
   *
   * Variant C is a TIER CHANGE + vitamin: the button also carries
   * data-fc-remove-key (the old cinnamon line). Like setTier, we ADD FIRST and
   * remove second — a failed add leaves the buyer's cart untouched.
   */
  function addUpsell(btn) {
    if (mutating) return;
    var items;
    try {
      items = JSON.parse(btn.getAttribute('data-fc-items') || '[]');
    } catch (e) {
      items = [];
    }
    if (!items.length) return;
    var removeKey = btn.getAttribute('data-fc-remove-key');
    mutating = true;
    btn.disabled = true;

    postJSON('/cart/add.js', { items: items })
      .then(function (state) {
        if (!removeKey) return state;
        var updates = {};
        updates[removeKey] = 0;
        return postJSON('/cart/update.js', { updates: updates });
      })
      .then(applySections)
      .catch(function () {
        btn.disabled = false;
        showError("We couldn't add that offer. Your cart is unchanged.");
      })
      .then(function () {
        mutating = false;
      });
  }

  /**
   * Plain quantity change for the upsold vitamin line (it has no pack ladder,
   * so the tier stepper does not apply). Quantity 0 removes the line, which
   * makes the offer cards render again on the next server pass.
   */
  function setQty(key, qty, btn) {
    if (!key || isNaN(qty) || qty < 0 || mutating) return;
    mutating = true;
    busy(key, true);
    if (btn) btn.disabled = true;

    var updates = {};
    updates[key] = qty;
    dropSPIfEmptying(updates);
    postJSON('/cart/update.js', { updates: updates })
      .then(applySections)
      .catch(function () {
        busy(key, false);
        if (btn) btn.disabled = false;
        showError("That didn't go through — please try again.");
      })
      .then(function () {
        mutating = false;
      });
  }

  /* ── Shipping protection (ABCDE v2) ─────────────────────────────────────
   * "Default ON": whenever its block is active (shown by the variant CSS) the
   * toggle reads ON, and on the live storefront the product is auto-added to the
   * cart. In the theme editor the toggle still shows ON but the cart is left
   * untouched (so editing doesn't pollute it). If the shopper turns it off,
   * that's remembered for the session and it won't re-add. One-off (no plan). */
  /* Add ONCE by default; if the shopper removes it, don't re-add. The dismissal
   * is persisted in sessionStorage so the removal sticks across further cart
   * changes and reloads for the rest of the visit. A brand-new session starts
   * on-by-default again. */
  var SP_OFF_KEY = 'fcSpDismissed';

  function spDismissed() {
    try { return sessionStorage.getItem(SP_OFF_KEY) === '1'; } catch (e) { return false; }
  }
  function setSpDismissed(v) {
    try { v ? sessionStorage.setItem(SP_OFF_KEY, '1') : sessionStorage.removeItem(SP_OFF_KEY); } catch (e) {}
  }
  function spBlock() {
    return document.querySelector('#CartDrawer .fc-sp-block');
  }
  function spActive(block) {
    // Own computed display — true when the variant CSS reveals it, regardless
    // of whether the drawer itself is currently open.
    return !!block && window.getComputedStyle(block).display !== 'none';
  }
  function setSPVisual(block, on) {
    if (!block) return;
    block.classList.toggle('is-on', !!on);
    var t = block.querySelector('[data-fc-sp-toggle]');
    if (t) t.setAttribute('aria-checked', on ? 'true' : 'false');
  }
  function inDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* Reconcile the toggle's visual state with the default-ON intent, and auto-add
   * on the storefront. */

  /* VISUAL only — safe to run synchronously inside applySections (i.e. while a
   * mutation is still in flight). ON by default unless the shopper dismissed it. */
  function reflectSP() {
    var block = spBlock();
    if (!block || !spActive(block)) return;
    if (block.getAttribute('data-fc-sp-incart') === 'true') { setSPVisual(block, true); return; }
    // Opt-in mode: not in the cart means the toggle reads OFF.
    if (block.hasAttribute('data-fc-sp-optin')) { setSPVisual(block, false); return; }
    setSPVisual(block, !spDismissed());
  }

  /* The actual auto-add. DEFERRED via scheduleSPAutoAdd so it runs AFTER the
   * triggering mutation has released `mutating` — otherwise, being called from
   * inside applySections (mid-mutation), it would always bail on the guard and
   * the product would never be added despite the toggle reading ON. */
  function autoAddSP() {
    if (inDesignMode()) return;
    if (mutating) { scheduleSPAutoAdd(); return; }   // still busy → try again next tick
    var block = spBlock();
    if (!block || !spActive(block)) return;
    if (block.getAttribute('data-fc-sp-incart') === 'true') return;
    if (block.hasAttribute('data-fc-sp-optin')) return;   // opt-in mode: never auto-add
    if (spDismissed()) return;
    var id = block.getAttribute('data-fc-sp-variant');
    if (!id) return;
    mutating = true;
    postJSON('/cart/add.js', spAddBody(block, id))
      .then(applySections)
      .catch(function () {})
      .then(function () { mutating = false; });
  }

  // Shipping protection adds as a subscription when the product carries a plan.
  function spAddBody(block, id) {
    var body = { id: id, quantity: 1 };
    var plan = block.getAttribute('data-fc-sp-plan');
    if (plan) body.selling_plan = plan;
    return body;
  }

  var spScheduled = false;
  function scheduleSPAutoAdd() {
    if (spScheduled) return;
    spScheduled = true;
    setTimeout(function () { spScheduled = false; autoAddSP(); }, 0);
  }

  /* Ev2 free gift: if the server flagged the gift line as orphaned (the cinnamon
   * was downgraded below the qualifying tier), remove it. Deferred like the SP
   * add, for the same mutating-flag reason. */
  function autoRemoveGift() {
    if (inDesignMode()) return;
    if (mutating) { scheduleGiftReconcile(); return; }
    var shell = document.querySelector('#CartDrawer .mcd-shell');
    var key = shell && shell.getAttribute('data-fc-gift-orphan');
    if (!key) return;
    mutating = true;
    var updates = {};
    updates[key] = 0;
    postJSON('/cart/update.js', { updates: updates })
      .then(applySections)
      .catch(function () {})
      .then(function () { mutating = false; });
  }

  var giftScheduled = false;
  function scheduleGiftReconcile() {
    if (giftScheduled) return;
    giftScheduled = true;
    setTimeout(function () { giftScheduled = false; autoRemoveGift(); }, 0);
  }

  /* Shipping protection is kept out of the line list, so a cart holding ONLY the
   * SP product isn't `cart == empty` and would show the non-empty layout with no
   * visible items. When the server flags SP as the lone item, remove it so the
   * real empty-cart state shows. Not a dismissal — SP re-adds on the next
   * product. Deferred like the other reconcilers. */
  function autoRemoveSPOnly() {
    if (inDesignMode()) return;
    if (mutating) { scheduleSPOnlyRemove(); return; }
    var shell = document.querySelector('#CartDrawer .mcd-shell');
    var key = shell && shell.getAttribute('data-fc-sp-only');
    if (!key) return;
    mutating = true;
    var updates = {};
    updates[key] = 0;
    postJSON('/cart/update.js', { updates: updates })
      .then(applySections)
      .catch(function () {})
      .then(function () { mutating = false; });
  }

  var spOnlyScheduled = false;
  function scheduleSPOnlyRemove() {
    if (spOnlyScheduled) return;
    spOnlyScheduled = true;
    setTimeout(function () { spOnlyScheduled = false; autoRemoveSPOnly(); }, 0);
  }

  /* Kaching bundle orphan guard (safety net for EVERY path): the server flags
   * gift lines left without their paid line (data-fc-bundle-orphan = comma-sep
   * keys) — e.g. the paid line removed from the native cart, or a stale cart.
   * Remove them so a $0 gifts-only checkout is impossible. Deferred like the
   * other reconcilers. */
  function autoRemoveBundleOrphans() {
    if (inDesignMode()) return;
    if (mutating) { scheduleBundleOrphanRemove(); return; }
    var shell = document.querySelector('#CartDrawer .mcd-shell');
    var raw = shell && shell.getAttribute('data-fc-bundle-orphan');
    if (!raw) return;
    var keys = raw.split(',').filter(Boolean);
    if (!keys.length) return;
    mutating = true;
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    dropSPIfEmptying(updates);
    postJSON('/cart/update.js', { updates: updates })
      .then(applySections)
      .catch(function () {})
      .then(function () { mutating = false; });
  }

  var bundleOrphanScheduled = false;
  function scheduleBundleOrphanRemove() {
    if (bundleOrphanScheduled) return;
    bundleOrphanScheduled = true;
    setTimeout(function () { bundleOrphanScheduled = false; autoRemoveBundleOrphans(); }, 0);
  }

  function syncSP() {
    reflectSP();
    scheduleSPAutoAdd();
    scheduleGiftReconcile();
    scheduleSPOnlyRemove();
    scheduleBundleOrphanRemove();
  }

  function toggleSP(block, btn) {
    if (!block || mutating) return;
    var turningOn = !block.classList.contains('is-on');
    setSPVisual(block, turningOn);            // reflect intent immediately
    setSpDismissed(!turningOn);
    if (inDesignMode()) return;               // editor: visual only, leave the cart alone

    var inCart = block.getAttribute('data-fc-sp-incart') === 'true';
    if (turningOn === inCart) return;         // cart already in the desired state
    if (btn) btn.disabled = true;
    mutating = true;
    var req;
    if (turningOn) {
      req = postJSON('/cart/add.js', spAddBody(block, block.getAttribute('data-fc-sp-variant')));
    } else {
      var updates = {};
      updates[block.getAttribute('data-fc-sp-key')] = 0;
      req = postJSON('/cart/update.js', { updates: updates });
    }
    req
      .then(applySections)
      .catch(function () {
        if (btn) btn.disabled = false;
        showError("That didn't go through — please try again.");
      })
      .then(function () { mutating = false; });
  }

  document.addEventListener('click', function (event) {
    var spToggle = event.target.closest('[data-fc-sp-toggle]');
    if (spToggle && !spToggle.hasAttribute('disabled')) {
      event.preventDefault();
      toggleSP(spToggle.closest('.fc-sp-block'), spToggle);
      return;
    }

    var remove = event.target.closest('[data-mcd-remove]');
    if (remove) {
      event.preventDefault();
      removeLine(remove.getAttribute('data-key'), remove);
      return;
    }

    var upsell = event.target.closest('[data-fc-upsell-add]');
    if (upsell && !upsell.hasAttribute('disabled')) {
      event.preventDefault();
      addUpsell(upsell);
      return;
    }

    var bundleQty = event.target.closest('[data-fc-bundle-qty]');
    if (bundleQty && !bundleQty.hasAttribute('disabled')) {
      event.preventDefault();
      setBundleQty(
        bundleQty.getAttribute('data-key'),
        parseInt(bundleQty.getAttribute('data-qty'), 10),
        parseInt(bundleQty.getAttribute('data-fc-cur-qty'), 10),
        bundleQty
      );
      return;
    }

    var qtyBtn = event.target.closest('[data-fc-qty]');
    if (qtyBtn && !qtyBtn.hasAttribute('disabled')) {
      event.preventDefault();
      setQty(qtyBtn.getAttribute('data-key'), parseInt(qtyBtn.getAttribute('data-qty'), 10), qtyBtn);
      return;
    }

    var tier = event.target.closest('[data-mcd-tier]');
    if (tier && !tier.hasAttribute('disabled')) {
      event.preventDefault();
      setTier(
        tier.getAttribute('data-key'),
        tier.getAttribute('data-to'),
        tier.getAttribute('data-plan'),
        tier
      );
    }
  });

  // Default-on shipping protection: evaluate once on initial load too (the
  // block's activation CSS is applied before this deferred script runs).
  syncSP();

  // The cart also changes via the theme's NATIVE flow (PDP add-to-cart, Dawn
  // quantity edits) which re-renders the drawer WITHOUT calling our
  // applySections — so reconcile SP after any drawer re-render, not just ours.
  // syncSP is idempotent and guarded (in-cart / dismissed / mutating), so the
  // observer firing on our own re-renders is a harmless no-op.
  (function () {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !window.MutationObserver) return;
    new MutationObserver(function () { syncSP(); }).observe(drawer, { childList: true, subtree: true });
  })();
})();
